import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Alert, Button, Form, ProgressBar } from 'react-bootstrap';
import { authenticatedIdentity } from '../../_services/apiFetch';
import { machiningMasterDataService as service } from '../../_services/machiningMasterData.service';

const sizeLabel = (bytes) => (bytes < 1024 ? `${bytes} bytes` : `${(bytes / 1024).toFixed(1)} KB`);

export const TechnicalSpecifications = forwardRef(function TechnicalSpecifications(
    { kind, asset, disabled, onBusy, onError, readOnly = false },
    ref
) {
    const [documents, setDocuments] = useState(asset?.technicalSpecifications || []);
    const [pending, setPending] = useState([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const lifecycle = useRef({
        mounted: true,
        identity: authenticatedIdentity(),
        busy: false,
        next: 0,
        urls: new Set(),
    });
    const current = () =>
        lifecycle.current.mounted && lifecycle.current.identity === authenticatedIdentity();

    useEffect(() => {
        lifecycle.current.mounted = true;

        return () => {
            lifecycle.current.mounted = false;
            lifecycle.current.urls.forEach((url) => URL.revokeObjectURL(url));
            lifecycle.current.urls.clear();
        };
    }, []);

    function failure(err) {
        if (!lifecycle.current.mounted) return;
        if (err.status === 401 || err.status === 403) onError?.(err);
        if (current()) setError(err.message || 'Document request failed.');
    }

    function begin() {
        if (!current() || lifecycle.current.busy) return false;

        lifecycle.current.busy = true;
        setBusy(true);
        onBusy(true);
        setError('');

        return true;
    }

    function end() {
        lifecycle.current.busy = false;

        if (current()) {
            setBusy(false);
            onBusy(false);
        }
    }

    function select(event) {
        const files = Array.from(event.target.files || []);
        event.target.value = '';

        if (documents.length + pending.length + files.length > 5) {
            setError('An asset can have at most five technical specification documents.');
            return;
        }

        if (files.some((file) => file.size > 10 * 1024 * 1024)) {
            setError('Each document must be at most 10 MB.');
            return;
        }

        setError('');
        setPending((previous) => [
            ...previous,
            ...files.map((file) => ({
                key: ++lifecycle.current.next,
                file,
                progress: 0,
                status: 'pending',
                error: '',
            })),
        ]);
    }

    const update = (key, values) => {
        if (current())
            setPending((previous) =>
                previous.map((item) => (item.key === key ? { ...item, ...values } : item))
            );
    };

    async function uploadPending(id = asset?.id, onlyKey) {
        if (!id || !begin()) return false;

        let succeeded = true;

        try {
            for (const item of pending.filter(
                (item) => onlyKey === undefined || onlyKey === item.key
            )) {
                if (!current()) return false;

                update(item.key, { status: 'uploading', progress: 0, error: '' });

                try {
                    const collection = await service.uploadAttachments(
                        kind,
                        id,
                        [item.file],
                        (progress) => update(item.key, { progress })
                    );

                    if (!current()) return false;

                    setDocuments(collection);
                    setPending((previous) => previous.filter((entry) => entry.key !== item.key));
                } catch (err) {
                    succeeded = false;
                    update(item.key, { status: 'error', error: err.message });

                    if (err.status === 401 || err.status === 403) {
                        failure(err);
                        break;
                    }
                }
            }
        } finally {
            end();
        }

        return succeeded;
    }

    useImperativeHandle(ref, () => ({ hasPending: () => pending.length > 0, uploadPending }));

    async function remove(document) {
        if (!begin()) return;

        try {
            const remaining = await service.deleteAttachment(kind, asset.id, document.id);
            if (current()) setDocuments(remaining);
        } catch (err) {
            failure(err);
        } finally {
            end();
        }
    }

    async function download(document, open = false) {
        if (!current()) return;

        try {
            const blob = await service.downloadAttachment(kind, asset.id, document.id);

            if (!current()) return;

            const url = URL.createObjectURL(blob);
            lifecycle.current.urls.add(url);
            const link = window.document.createElement('a');
            link.href = url;

            if (open) {
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
            } else link.download = document.originalName;

            window.document.body.appendChild(link);
            link.click();
            link.remove();

            if (!open) {
                URL.revokeObjectURL(url);
                lifecycle.current.urls.delete(url);
            }
        } catch (err) {
            failure(err);
        }
    }
    
    return (
        <section aria-label="Technical specifications" className="border-top pt-3">
            <h5>Technical specifications</h5>
            <p className="text-muted">
                Up to five PDF or image documents, 10 MB each.{' '}
                {asset?.id ? '' : 'Documents upload after the asset is saved.'}
            </p>
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {documents.length ? (
                <ul className="list-unstyled">
                    {documents.map((document) => (
                        <li key={document.id} className="mb-3">
                            <div className="text-break">
                                {document.originalName}{' '}
                                <span className="text-muted">
                                    ({document.mimeType}, {sizeLabel(document.sizeBytes)})
                                </span>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-1">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={disabled || busy}
                                    onClick={() => download(document, true)}
                                    aria-label={`Open ${document.originalName}`}
                                >
                                    Open
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-primary"
                                    disabled={disabled || busy}
                                    onClick={() => download(document)}
                                    aria-label={`Download ${document.originalName}`}
                                >
                                    Download
                                </Button>
                                {!readOnly && <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-danger"
                                    disabled={disabled || busy}
                                    onClick={() => remove(document)}
                                    aria-label={`Remove ${document.originalName}`}
                                >
                                    Remove
                                </Button>}
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <p>No documents attached.</p>
            )}
            {!readOnly && <Form.Group controlId="technical-specification-files" className="mb-3">
                <Form.Label>Technical specification documents</Form.Label>
                <Form.Control
                    type="file"
                    multiple
                    accept="application/pdf,image/jpeg,image/png,image/webp,image/gif,image/tiff,image/avif"
                    disabled={disabled || busy || documents.length + pending.length >= 5}
                    onChange={select}
                />
            </Form.Group>}
            {pending.map((item) => (
                <div key={item.key} className="mb-3">
                    <div className="text-break">
                        {item.file.name}{' '}
                        <span className="text-muted">({sizeLabel(item.file.size)})</span>
                    </div>
                    {item.status === 'uploading' ? (
                        <>
                            <ProgressBar now={item.progress} />
                            <span role="status">
                                {item.progress}% ·{' '}
                                {item.progress === 100 ? 'Confirming…' : 'Uploading…'}
                            </span>
                        </>
                    ) : item.status === 'error' ? (
                        <>
                            <div role="alert" className="text-danger">
                                {item.error}
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                disabled={disabled || busy}
                                onClick={() => uploadPending(asset.id, item.key)}
                                aria-label={`Retry ${item.file.name}`}
                            >
                                Retry
                            </Button>
                        </>
                    ) : (
                        <span>Pending upload</span>
                    )}
                    <Button
                        type="button"
                        size="sm"
                        variant="link"
                        disabled={disabled || busy}
                        onClick={() =>
                            setPending((previous) =>
                                previous.filter((entry) => entry.key !== item.key)
                            )
                        }
                        aria-label={`Discard ${item.file.name}`}
                    >
                        Discard
                    </Button>
                </div>
            ))}
            {asset?.id && pending.some((item) => item.status === 'pending') ? (
                <Button type="button" disabled={disabled || busy} onClick={() => uploadPending()}>
                    Upload documents
                </Button>
            ) : null}
        </section>
    );
});
